// Portfolio Administration v1.0 PR 4a §7.4 — per-policy detail drawer.
// Hosts read-only metadata + Phoenix / Seller "named as" verification
// actions. Update / edit of policy metadata is reachable via the inline
// "Edit policy" toggle (server-side gate: Insurance.Verify).

import { useState } from "react";
import { Link } from "react-router-dom";
import { Button, Badge } from "@/design-system/components/ui";
import { useToast } from "@/components/ui/Toast";
import { useAuth } from "@/hooks/useAuth";
import { ApiError } from "@/api/client";
import {
  useLoanDueDates,
  useLoanParties,
  useUpdateInsurancePolicy,
  useVerifyInsurancePolicy,
} from "@/hooks/portfolioAdmin/usePortfolioAdmin";
import { ResourceDrawer } from "../common/ResourceDrawer";
import { calendarDate, daysLabel, daysUntil, formatDate } from "../common/colorBand";
import { isLapsed } from "./policyStatus";
import type {
  InsurancePolicyDto,
  UpsertInsurancePolicyRequest,
} from "@/api/portfolioAdmin/types";

const KEY_PERSON_POLICY_TYPES = new Set(["KeyPersonLife", "KeyPersonDisability"]);

interface InsurancePolicyDrawerProps {
  loanId: string;
  policy: InsurancePolicyDto;
  open: boolean;
  onClose: () => void;
}

function isoFromDateTime(iso: string | null): string {
  if (!iso) return "";
  return iso.slice(0, 10);
}

export function InsurancePolicyDrawer({
  loanId,
  policy,
  open,
  onClose,
}: InsurancePolicyDrawerProps) {
  const toast = useToast();
  const auth = useAuth();
  const canVerify =
    auth.hasPermission("Insurance.Verify") || auth.hasRoleCode("ADMIN");

  const update = useUpdateInsurancePolicy(loanId);
  const verify = useVerifyInsurancePolicy(loanId);

  // Renewal history — completed insurance-renewal calendar items tied to
  // this policy. Rendered only when rows exist.
  const { data: calendarItems } = useLoanDueDates(loanId);
  const renewalHistory = (calendarItems ?? []).filter(
    (i) =>
      i.sourceType === "insurance-renewal" &&
      i.sourceEntityId === policy.id &&
      i.status === "Completed",
  );

  // Key-person roster — only fetched/shown for key-person policy types.
  const isKeyPersonPolicy = KEY_PERSON_POLICY_TYPES.has(policy.policyType);
  const { data: parties } = useLoanParties(isKeyPersonPolicy ? loanId : undefined);
  const keyPersons = (parties ?? []).filter((p) => p.isKeyPerson);

  const [editing, setEditing] = useState(false);
  const [carrier, setCarrier] = useState(policy.carrierName ?? "");
  const [policyNumber, setPolicyNumber] = useState(policy.policyNumber ?? "");
  const [coverage, setCoverage] = useState(
    policy.coverageAmount?.toString() ?? "",
  );
  const [effective, setEffective] = useState(isoFromDateTime(policy.effectiveDate));
  const [expiration, setExpiration] = useState(isoFromDateTime(policy.expirationDate));

  const surfaceError = (err: unknown, fallback: string) => {
    toast.error(
      err instanceof ApiError && err.errors.length
        ? err.errors.join("; ")
        : err instanceof Error
          ? err.message
          : fallback,
    );
  };

  const onSave = async () => {
    if (!expiration) {
      toast.error("Expiration date is required.");
      return;
    }
    const body: UpsertInsurancePolicyRequest = {
      policyType: policy.policyType,
      carrierName: carrier || null,
      policyNumber: policyNumber || null,
      coverageAmount: coverage ? Number(coverage) : null,
      effectiveDate: effective || null,
      expirationDate: expiration,
    };
    try {
      await update.mutateAsync({ policyId: policy.id, body });
      toast.success("Policy updated.");
      setEditing(false);
    } catch (err) {
      surfaceError(err, "Could not update policy.");
    }
  };

  const onVerifyPhoenix = async () => {
    try {
      await verify.mutateAsync({ policyId: policy.id, party: "phoenix" });
      toast.success("Phoenix named-as verification recorded.");
    } catch (err) {
      surfaceError(err, "Could not verify Phoenix named-as.");
    }
  };
  const onVerifySeller = async () => {
    try {
      await verify.mutateAsync({ policyId: policy.id, party: "seller" });
      toast.success("Seller named-as verification recorded.");
    } catch (err) {
      surfaceError(err, "Could not verify Seller named-as.");
    }
  };

  const days = daysUntil(policy.expirationDate);

  return (
    <ResourceDrawer
      open={open}
      onClose={onClose}
      dismissOnBackdropClick={false}
      title={policy.policyType}
      subtitle={`Carrier: ${policy.carrierName ?? "—"} · Policy #${policy.policyNumber ?? "—"}`}
      testId="insurance-drawer"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>Close</Button>
          {editing ? (
            <Button
              variant="primary"
              onClick={() => void onSave()}
              disabled={update.isPending}
              data-testid="insurance-save"
            >
              Save
            </Button>
          ) : canVerify ? (
            <Button
              variant="secondary"
              onClick={() => setEditing(true)}
              data-testid="insurance-edit-toggle"
            >
              Edit policy
            </Button>
          ) : null}
        </>
      }
    >
      <div className="flex flex-col gap-space-4" data-testid="insurance-drawer-body">
        <section>
          <h3 className="font-display font-semibold text-h4 m-0 mb-space-2">
            Policy metadata
          </h3>
          {editing ? (
            <div className="grid grid-cols-2 gap-space-2">
              <label className="flex flex-col gap-space-1 text-caption">
                <span className="text-gray-mid uppercase tracking-label">Carrier</span>
                <input
                  className="h-9 px-space-2 border border-border-light rounded-md text-small"
                  value={carrier}
                  onChange={(e) => setCarrier(e.target.value)}
                />
              </label>
              <label className="flex flex-col gap-space-1 text-caption">
                <span className="text-gray-mid uppercase tracking-label">Policy #</span>
                <input
                  className="h-9 px-space-2 border border-border-light rounded-md text-small font-mono"
                  value={policyNumber}
                  onChange={(e) => setPolicyNumber(e.target.value)}
                />
              </label>
              <label className="flex flex-col gap-space-1 text-caption">
                <span className="text-gray-mid uppercase tracking-label">Coverage ($)</span>
                <input
                  type="number"
                  className="h-9 px-space-2 border border-border-light rounded-md text-small"
                  value={coverage}
                  onChange={(e) => setCoverage(e.target.value)}
                />
              </label>
              <label className="flex flex-col gap-space-1 text-caption">
                <span className="text-gray-mid uppercase tracking-label">Effective</span>
                <input
                  type="date"
                  className="h-9 px-space-2 border border-border-light rounded-md text-small"
                  value={effective}
                  onChange={(e) => setEffective(e.target.value)}
                />
              </label>
              <label className="flex flex-col gap-space-1 text-caption">
                <span className="text-gray-mid uppercase tracking-label">Expiration</span>
                <input
                  type="date"
                  className="h-9 px-space-2 border border-border-light rounded-md text-small"
                  value={expiration}
                  onChange={(e) => setExpiration(e.target.value)}
                />
              </label>
            </div>
          ) : (
            <dl className="grid grid-cols-2 gap-y-space-1 text-small m-0">
              <dt className="text-caption text-gray-mid uppercase tracking-label">Carrier</dt>
              <dd className="m-0">{policy.carrierName ?? "—"}</dd>
              <dt className="text-caption text-gray-mid uppercase tracking-label">Policy #</dt>
              <dd className="m-0 font-mono">{policy.policyNumber ?? "—"}</dd>
              <dt className="text-caption text-gray-mid uppercase tracking-label">Coverage</dt>
              <dd className="m-0">
                {policy.coverageAmount != null ? `$${policy.coverageAmount.toLocaleString()}` : "—"}
              </dd>
              <dt className="text-caption text-gray-mid uppercase tracking-label">Effective</dt>
              <dd className="m-0">{calendarDate(policy.effectiveDate)}</dd>
              <dt className="text-caption text-gray-mid uppercase tracking-label">Expiration</dt>
              <dd className="m-0">
                {calendarDate(policy.expirationDate)} ({daysLabel(days)})
              </dd>
              <dt className="text-caption text-gray-mid uppercase tracking-label">Status</dt>
              <dd className="m-0">
                <Badge status={isLapsed(policy) ? "error" : "finalized"}>
                  {policy.status}
                </Badge>
              </dd>
            </dl>
          )}
        </section>

        <section>
          <h3 className="font-display font-semibold text-h4 m-0 mb-space-2">
            Required-party verification
          </h3>
          <div className="grid grid-cols-2 gap-space-2">
            <div className="rounded-md border border-border-light p-space-2">
              <div className="text-caption text-gray-mid uppercase tracking-label">
                Phoenix named-as
              </div>
              <div className="text-small mt-space-1">
                {policy.phoenixNamedAsVerifiedDate
                  ? `Verified ${formatDate(policy.phoenixNamedAsVerifiedDate)}`
                  : "Not verified"}
              </div>
              {canVerify && !policy.phoenixNamedAsVerifiedDate && (
                <Button
                  variant="secondary"
                  size="sm"
                  className="mt-space-2"
                  onClick={() => void onVerifyPhoenix()}
                  disabled={verify.isPending}
                  data-testid="insurance-verify-phoenix"
                >
                  Verify Phoenix
                </Button>
              )}
            </div>
            <div className="rounded-md border border-border-light p-space-2">
              <div className="text-caption text-gray-mid uppercase tracking-label">
                Seller named-as
              </div>
              <div className="text-small mt-space-1">
                {policy.sellerNamedAsVerifiedDate
                  ? `Verified ${formatDate(policy.sellerNamedAsVerifiedDate)}`
                  : "Not verified"}
              </div>
              {canVerify && !policy.sellerNamedAsVerifiedDate && (
                <Button
                  variant="secondary"
                  size="sm"
                  className="mt-space-2"
                  onClick={() => void onVerifySeller()}
                  disabled={verify.isPending}
                  data-testid="insurance-verify-seller"
                >
                  Verify Seller
                </Button>
              )}
            </div>
          </div>
        </section>

        {renewalHistory.length > 0 && (
          <section data-testid="insurance-renewal-history">
            <h3 className="font-display font-semibold text-h4 m-0 mb-space-2">
              Renewal history
            </h3>
            <ul className="m-0 p-0 list-none flex flex-col gap-space-1">
              {renewalHistory.map((item) => (
                <li
                  key={item.id}
                  className="flex items-center justify-between gap-space-3 text-small"
                >
                  <span className="min-w-0 truncate">{item.title}</span>
                  <span className="shrink-0 text-caption text-gray-mid">
                    Completed {formatDate(item.completedDate)}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {isKeyPersonPolicy && (
          <section data-testid="insurance-key-persons">
            <h3 className="font-display font-semibold text-h4 m-0 mb-space-2">
              Key persons on this loan
            </h3>
            {keyPersons.length === 0 ? (
              <p className="text-small text-gray-mid italic m-0">
                No loan parties are flagged as key persons.
              </p>
            ) : (
              <ul className="m-0 p-0 list-none flex flex-col gap-space-1">
                {keyPersons.map((p) => (
                  <li key={p.id} className="text-small">
                    {p.counterpartyType === "Person" ? (
                      <Link
                        to={`/counterparties/persons/${p.counterpartyId}`}
                        className="text-onyx underline hover:text-gold-shadow"
                      >
                        {p.counterpartyName ?? "Unnamed person"}
                      </Link>
                    ) : (
                      <span>{p.counterpartyName ?? "Unnamed party"}</span>
                    )}
                    <span className="ml-space-2 text-caption text-gray-mid">
                      {p.roleOnLoan}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}

        <section className="rounded-md bg-white-warm p-space-2">
          <p className="text-caption text-gray-mid m-0">
            Premium tracker, document attachments, and coverage-change history
            ship with the Insurance v1.0 module follow-up — fields not yet
            wired end-to-end are intentionally not surfaced here. Renewal
            calendar items appear on the <strong>Due Dates</strong> tab
            filtered to <em>Insurance</em>.
          </p>
        </section>
      </div>
    </ResourceDrawer>
  );
}
